import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useContext,
} from "react";
import { Container, Spinner } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router";
import { getData } from "../../Actions/Product";
import { Cart_Actions } from "../../Actions/Cart";
import { refreshToken } from "../../Actions/Auth";
import { Common_Event_Hit } from "../../Actions/UserEvents";
import {
  MdDelete,
  MdArrowBack,
  MdCheckCircle,
  MdEdit,
  MdErrorOutline,
} from "react-icons/md";
import { cartDataContext } from "../../App";
import "./RequestMedicine.css";

const RequestMedicine = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const initialSearch = location.state?.searchString || "";
  const { cartData, setCartData } = useContext(cartDataContext);

  const [searchString, setSearchString] = useState(initialSearch);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);

  const [selectedMedicine, setSelectedMedicine] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const dropdownRef = useRef(null);
  const searchWrapperRef = useRef(null);

  const [medicineList, setMedicineList] = useState([]);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [editingCell, setEditingCell] = useState(null);

  const [stockPopup, setStockPopup] = useState({
    show: false,
    type: "",
    message: "",
    details: null,
  });
  const [addToCartLoading, setAddToCartLoading] = useState(false);

  const debounce = (func, delay) => {
    let timer;
    return (...args) => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => func(...args), delay);
    };
  };

  const fetchSuggestions = (query) => {
    const trimmed = query?.trim();
    if (!trimmed || trimmed.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    setSuggestionsLoading(true);
    setShowSuggestions(true);
    getData("PRODUCTS_BY_SEARCH", { page: 0 }, trimmed)
      .then((res) => {
        const isFallback = res.data?.source === "FALLBACK";
        const medicinesResponsesList = res.data?.medicinesResponsesList || [];
        const medicinesMap = Object.fromEntries(
          medicinesResponsesList.filter((m) => m.id).map((m) => [m.id, m]),
        );
        const round2 = (v) => (v != null ? Math.round(v * 100) / 100 : v);

        const list = isFallback
          ? (res.data?.suggestions || []).map((s) => {
              const isOutOfStock = !(s.availableQuantity > 0);
              const richData =
                isOutOfStock && medicinesMap[s.id] ? medicinesMap[s.id] : s;
              return {
                ...s,
                ptr: round2(richData.ptr),
                packaging: richData.packaging,
                mrp: round2(richData.mrp),
                schemes: richData.schemes,
                manufacturer: richData.manufacturer,
                cashDiscountInPercentage: richData.cashDiscountInPercentage,
                addInventory: true,
              };
            })
          : medicinesResponsesList.map((s) => ({
              ...s,
              ptr: round2(s.ptr),
              mrp: round2(s.mrp),
              addInventory: true,
            }));
        setSuggestions(list);
        setSuggestionsLoading(false);
      })
      .catch((error) => {
        setSuggestionsLoading(false);
        if (error?.response?.status === 401) {
          refreshToken().then(() => fetchSuggestions(query));
        }
      });
  };

  const debouncedFetch = useCallback(debounce(fetchSuggestions, 400), []);

  useEffect(() => {
    if (initialSearch) {
      fetchSuggestions(initialSearch);
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        searchWrapperRef.current &&
        !searchWrapperRef.current.contains(e.target)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (highlightedIndex >= 0 && dropdownRef.current) {
      const items = dropdownRef.current.querySelectorAll(".rm-suggestion-item");
      if (items[highlightedIndex]) {
        items[highlightedIndex].scrollIntoView({ block: "nearest" });
      }
    }
  }, [highlightedIndex]);

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev < suggestions.length - 1 ? prev + 1 : 0,
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev > 0 ? prev - 1 : suggestions.length - 1,
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      e.nativeEvent.stopImmediatePropagation();
      if (highlightedIndex >= 0 && suggestions[highlightedIndex]) {
        handleSelectMedicine(suggestions[highlightedIndex]);
      } else if (suggestions.length > 0) {
        handleSelectMedicine(suggestions[0]);
      }
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
      setHighlightedIndex(-1);
    }
  };

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchString(val);
    setSelectedMedicine(null);
    setHighlightedIndex(-1);
    debouncedFetch(val);
  };

  const handleSelectMedicine = (med) => {
    setSelectedMedicine(med);
    setSearchString(med.name || "");
    setShowSuggestions(false);
    setSuggestions([]);
    setHighlightedIndex(-1);
    setQuantity(1);
  };

  const getCartQty = (qty, schemes) => {
    const schemeStr = (schemes || [])[0] || "";
    const match = schemeStr.match(/^(\d+)\+(\d+)$/);
    if (!match) return qty;
    const x = parseInt(match[1]);
    const y = parseInt(match[2]);
    if (x <= 0) return qty;
    const effective = qty - Math.floor(qty / x) * y;
    return effective > 0 ? effective : qty;
  };

  const addToCartAPI = (med, qty) => {
    let data = localStorage.getItem("userData");
    data = JSON.parse(data);
    const onBehalfRetCode = localStorage.getItem("onBehalfRetCode");
    const activeRetailerCode =
      onBehalfRetCode &&
      onBehalfRetCode.length > 0 &&
      onBehalfRetCode !== "undefined"
        ? onBehalfRetCode
        : data.retailerCode;
    const params = {};
    const body = {
      androidMap: {
        locale: "en",
        source: "pharmacyWeb",
        deviceId: null,
        appVersion: navigator.userAgent,
        deviceName: null,
        screen_name: "request_medicine",
        appVersionCode: "156",
      },
      medicineId: med.id,
      quantity: qty,
      retailerCode: activeRetailerCode,
      retailerId: data.id,
      ...(med.addInventory ? { addInventory: true } : {}),
    };
    return Cart_Actions("ADD_TO_CART", params, body);
  };

  const handleAddMedicine = () => {
    if (!searchString.trim()) return;

    if (selectedMedicine && selectedMedicine.addToCartEnabled === false) {
      setStockPopup({
        show: true,
        type: "license_required",
        message:
          "To add this item you need to have a drug license. Contact customer support: 8390088888",
        details: null,
      });
      return;
    }

    const stock = selectedMedicine?.availableQuantity ?? 0;
    const reqQty = quantity || 1;

    if (
      selectedMedicine &&
      selectedMedicine.id &&
      stock > 0 &&
      reqQty <= stock
    ) {
      const cartQty = getCartQty(
        reqQty,
        selectedMedicine.schemes || selectedMedicine.scheme,
      );
      setAddToCartLoading(true);
      addToCartAPI(selectedMedicine, cartQty)
        .then((res) => {
          setCartData(res.data.data);
          setAddToCartLoading(false);
          setStockPopup({
            show: true,
            type: "full_cart",
            message: `"${selectedMedicine.name}" is available in stock! ${reqQty} unit(s) added to your cart.`,
            details: null,
          });
          setSearchString("");
          setSelectedMedicine(null);
          setQuantity(1);
          setSuggestions([]);
          setShowSuggestions(false);
        })
        .catch((error) => {
          setAddToCartLoading(false);
          if (error?.response?.status === 401) {
            refreshToken().then(() => handleAddMedicine());
          }
        });
      return;
    }

    if (
      selectedMedicine &&
      selectedMedicine.id &&
      stock > 0 &&
      reqQty > stock
    ) {
      const remainderQty = reqQty - stock;
      const partialCartQty = getCartQty(
        stock,
        selectedMedicine.schemes || selectedMedicine.scheme,
      );
      setAddToCartLoading(true);
      addToCartAPI(selectedMedicine, partialCartQty)
        .then((res) => {
          setCartData(res.data.data);
          setAddToCartLoading(false);

          const entry = {
            id: Date.now(),
            name: selectedMedicine.name || searchString.trim(),
            quantity: remainderQty,
            medicineId: selectedMedicine.id || null,
            cbMedicineId: selectedMedicine.cbMedicineId || null,
            pack: selectedMedicine.packaging || "-",
            schemes: selectedMedicine.scheme || [],
            mrp: selectedMedicine.mrp || null,
            ptr: selectedMedicine.ptr || null,
            stock: selectedMedicine.availableQuantity ?? null,
            manufacturer: selectedMedicine.manufacturer || "",
            cashDiscount: selectedMedicine.cashDiscountInPercentage || null,
            addInventory: selectedMedicine.addInventory === true,
          };
          setMedicineList((prev) => [...prev, entry]);

          setStockPopup({
            show: true,
            type: "partial",
            message: `Only ${stock} unit(s) of "${selectedMedicine.name}" available in stock.`,
            details: {
              cartQty: stock,
              requestQty: remainderQty,
              name: selectedMedicine.name,
            },
          });
          setSearchString("");
          setSelectedMedicine(null);
          setQuantity(1);
          setSuggestions([]);
          setShowSuggestions(false);
        })
        .catch((error) => {
          setAddToCartLoading(false);
          if (error?.response?.status === 401) {
            refreshToken().then(() => handleAddMedicine());
          }
        });
      return;
    }

    const entry = {
      id: Date.now(),
      name: selectedMedicine?.name || searchString.trim(),
      quantity: reqQty,
      medicineId: selectedMedicine?.id || null,
      cbMedicineId: selectedMedicine?.cbMedicineId || null,
      pack: selectedMedicine?.packaging || "-",
      schemes: selectedMedicine?.scheme || [],
      mrp: selectedMedicine?.mrp || null,
      ptr: selectedMedicine?.ptr || null,
      stock: selectedMedicine?.availableQuantity ?? null,
      manufacturer: selectedMedicine?.manufacturer || "",
      cashDiscount: selectedMedicine?.cashDiscountInPercentage || null,
      addInventory: selectedMedicine?.addInventory === true,
    };

    setMedicineList((prev) => [...prev, entry]);
    setSearchString("");
    setSelectedMedicine(null);
    setQuantity(1);
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const handleRemoveMedicine = (id) => {
    setMedicineList((prev) => prev.filter((item) => item.id !== id));
  };

  const startEdit = (id, field, currentValue) => {
    const value =
      field === "schemes"
        ? (currentValue || []).join(", ")
        : currentValue != null
          ? String(currentValue)
          : "";
    setEditingCell({ id, field, value });
  };

  const commitEdit = () => {
    if (!editingCell) return;
    const { id, field, value } = editingCell;
    setMedicineList((prev) =>
      prev.map((m) => {
        if (m.id !== id) return m;
        if (field === "schemes") {
          const arr = value
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean);
          return { ...m, requestedScheme: arr[0] || null };
        }
        return m;
      }),
    );
    setEditingCell(null);
  };

  const handleSubmitAll = () => {
    if (medicineList.length === 0) return;
    setSubmitLoading(true);

    medicineList.forEach((med) => {
      Common_Event_Hit("DYNAMIC_EVENT", {
        eventName: "request_medicine",
        request_medicine: med.name,
      });
    });

    let userData = localStorage.getItem("userData");
    userData = JSON.parse(userData);
    const onBehalfRetCode = localStorage.getItem("onBehalfRetCode");
    const activeRetailerCode =
      onBehalfRetCode &&
      onBehalfRetCode.length > 0 &&
      onBehalfRetCode !== "undefined"
        ? onBehalfRetCode
        : userData.retailerCode;

    const payload = medicineList.map((med) => ({
      medicineName: med.name,
      quantity: med.quantity,
      retailerCode: activeRetailerCode,
      addInventory: med.addInventory === true,
      medicineId: med.medicineId || null,
      cbMedicineId: med.cbMedicineId || null,
      scheme: (med.schemes || [])[0] || null,
      ...(med.requestedScheme != null
        ? { requestedScheme: med.requestedScheme }
        : {}),
      ptr: med.ptr || null,
      mrp: med.mrp || null,
    }));

    getData("REQUEST_PRODUCT", { retailerCode: activeRetailerCode }, payload)
      .then(() => {
        setSubmitLoading(false);
        setSubmitSuccess(true);
        setMedicineList([]);
      })
      .catch((error) => {
        setSubmitLoading(false);
        if (error?.response?.status === 401) {
          refreshToken().then(() => handleSubmitAll());
        }
      });
  };

  return (
    <div className="request-medicine-page">
      <Container className="request-medicine-container">
        <div className="rm-header">
          <button className="rm-back-btn" onClick={() => navigate(-1)}>
            <MdArrowBack size="1.4rem" />
          </button>
          <h2 className="rm-title">Request Medicines</h2>
        </div>

        <div className="rm-card rm-search-card">
          <h3 className="rm-card-title">Search & Add Medicines</h3>

          <div className="rm-search-wrapper" ref={searchWrapperRef}>
            <input
              type="text"
              className="rm-search-input"
              placeholder="Search medicines by name..."
              value={searchString}
              onChange={handleSearchChange}
              onKeyDown={handleKeyDown}
              onFocus={() => {
                if (suggestions.length > 0) setShowSuggestions(true);
              }}
              autoComplete="off"
            />

            {showSuggestions && (
              <div className="rm-suggestions-dropdown" ref={dropdownRef}>
                {suggestionsLoading ? (
                  <div className="rm-suggestion-loading">
                    <Spinner animation="border" size="sm" /> Searching...
                  </div>
                ) : (
                  <>
                    {suggestions.length > 0 ? (
                      suggestions.map((med, index) => (
                        <div
                          key={med.id || index}
                          className={`rm-suggestion-item${
                            index === highlightedIndex
                              ? " rm-suggestion-highlighted"
                              : ""
                          }`}
                          onClick={() => handleSelectMedicine(med)}
                          onMouseEnter={() => setHighlightedIndex(index)}>
                          <span className="rm-suggestion-name">{med.name}</span>
                          <div className="rm-suggestion-details">
                            {med.packaging && (
                              <span className="rm-suggestion-pack">
                                {med.packaging}
                              </span>
                            )}
                            <span
                              className={`rm-suggestion-stock ${
                                med.availableQuantity > 0
                                  ? "in-stock"
                                  : "out-of-stock"
                              }`}>
                              {med.availableQuantity > 0
                                ? `Stock: ${med.availableQuantity}`
                                : "Out of stock"}
                            </span>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="rm-suggestion-empty">
                        No medicines found for "{searchString.trim()}".
                      </div>
                    )}
                    {suggestions.length < 5 &&
                      searchString.trim().length > 0 && (
                        <div className="rm-suggestion-request-row">
                          <button
                            className="rm-request-btn"
                            onClick={() => {
                              setSelectedMedicine(null);
                              setSearchString(searchString.trim());
                              setShowSuggestions(false);
                              setSuggestions([]);
                            }}>
                            + Request "{searchString.trim()}"
                          </button>
                        </div>
                      )}
                  </>
                )}
              </div>
            )}
          </div>

          {selectedMedicine && (
            <div className="rm-selected-info">
              <span className="rm-selected-label">Stock Available:</span>
              <span
                className={`rm-selected-stock ${
                  selectedMedicine.availableQuantity > 0
                    ? "in-stock"
                    : "out-of-stock"
                }`}>
                {selectedMedicine.availableQuantity > 0
                  ? selectedMedicine.availableQuantity
                  : "Out of stock"}
              </span>
            </div>
          )}

          <div className="rm-options-row">
            <div className="rm-field rm-qty-field">
              <label className="rm-label">Quantity</label>
              <input
                type="number"
                className="rm-qty-input"
                min="1"
                value={quantity}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === "") {
                    setQuantity("");
                  } else {
                    const num = parseInt(val);
                    if (!isNaN(num) && num > 0) setQuantity(num);
                  }
                }}
                onBlur={() => {
                  if (!quantity) setQuantity(1);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    e.nativeEvent.stopImmediatePropagation();
                    handleAddMedicine();
                  }
                }}
              />
            </div>
            <div className="rm-field rm-add-field">
              <button
                className="rm-add-btn"
                onClick={handleAddMedicine}
                disabled={!searchString.trim() || addToCartLoading}>
                {addToCartLoading ? (
                  <>
                    <Spinner animation="border" size="sm" /> Adding...
                  </>
                ) : (
                  "+ Add Medicine"
                )}
              </button>
            </div>
          </div>
        </div>

        <div className="rm-card rm-list-card">
          <h3 className="rm-card-title">
            Requested Medicines
            {medicineList.length > 0 && (
              <span className="rm-count-badge">{medicineList.length}</span>
            )}
          </h3>

          {medicineList.length === 0 ? (
            <div className="rm-empty-list">
              <p>No medicines added yet.</p>
              <p className="rm-empty-hint">
                Search and add medicines above to request them.
              </p>
            </div>
          ) : (
            <>
              <div className="rm-table-header">
                <span className="rm-th rm-th-name">NAME</span>
                <span className="rm-th rm-th-pack">PACK</span>
                <span className="rm-th rm-th-scheme">SCHEMES</span>
                <span className="rm-th rm-th-ptr">PTR</span>
                <span className="rm-th rm-th-mrp">MRP</span>
                <span className="rm-th rm-th-stock">STOCK</span>
                <span className="rm-th rm-th-qty">QTY</span>
                <span className="rm-th rm-th-action"></span>
              </div>

              <div className="rm-medicine-list">
                {medicineList.map((med, index) => (
                  <div key={med.id} className="rm-medicine-item">
                    <div className="rm-td rm-td-name">
                      <span className="rm-medicine-name">{med.name}</span>
                      {med.manufacturer && (
                        <span className="rm-medicine-manufacturer">
                          {med.manufacturer}
                        </span>
                      )}
                      {med.cashDiscount > 0 && (
                        <span className="rm-cd-badge">
                          {med.cashDiscount}% CD
                        </span>
                      )}
                    </div>
                    <div className="rm-td rm-td-pack">{med.pack}</div>
                    <div
                      className="rm-td rm-td-scheme rm-td-editable"
                      onClick={() =>
                        !editingCell &&
                        startEdit(
                          med.id,
                          "schemes",
                          med.requestedScheme != null
                            ? [med.requestedScheme]
                            : med.schemes,
                        )
                      }>
                      {editingCell?.id === med.id &&
                      editingCell?.field === "schemes" ? (
                        <input
                          className="rm-inline-input"
                          autoFocus
                          value={editingCell.value}
                          onChange={(e) =>
                            setEditingCell((prev) => ({
                              ...prev,
                              value: e.target.value,
                            }))
                          }
                          onBlur={commitEdit}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              commitEdit();
                            } else if (e.key === "Escape") {
                              setEditingCell(null);
                            }
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      ) : (
                        <span className="rm-editable-value">
                          {med.requestedScheme != null
                            ? med.requestedScheme
                            : med.schemes && med.schemes.length > 0
                              ? med.schemes.join(", ")
                              : "-"}
                          <MdEdit className="rm-edit-icon" size="0.8rem" />
                        </span>
                      )}
                    </div>
                    <div className="rm-td rm-td-ptr">
                      {med.ptr != null ? `₹${med.ptr}` : "-"}
                    </div>
                    <div className="rm-td rm-td-mrp">
                      {med.mrp != null ? `₹${med.mrp}` : "-"}
                    </div>
                    <div className="rm-td rm-td-stock">
                      {med.stock != null ? med.stock : "-"}
                    </div>
                    <div className="rm-td rm-td-qty">{med.quantity}</div>
                    <div className="rm-td rm-td-action">
                      <button
                        className="rm-delete-btn"
                        onClick={() => handleRemoveMedicine(med.id)}>
                        <MdDelete size="1.2rem" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <button
                className="rm-submit-btn"
                onClick={handleSubmitAll}
                disabled={submitLoading}>
                {submitLoading ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  `Submit Request (${medicineList.length} medicine${medicineList.length > 1 ? "s" : ""})`
                )}
              </button>
            </>
          )}

          {submitSuccess && (
            <div className="rm-success-msg">
              Your medicine request has been submitted successfully!
            </div>
          )}
        </div>

        {stockPopup.show && (
          <div
            className="rm-popup-overlay"
            onClick={() =>
              setStockPopup({
                show: false,
                type: "",
                message: "",
                details: null,
              })
            }>
            <div className="rm-popup" onClick={(e) => e.stopPropagation()}>
              <div className="rm-popup-icon">
                {stockPopup.type === "license_required" ? (
                  <MdErrorOutline size="3rem" color="#f44336" />
                ) : (
                  <MdCheckCircle
                    size="3rem"
                    color={
                      stockPopup.type === "full_cart" ? "#4caf50" : "#ff9800"
                    }
                  />
                )}
              </div>
              <h3 className="rm-popup-title">
                {stockPopup.type === "full_cart"
                  ? "Added to Cart!"
                  : stockPopup.type === "license_required"
                    ? "Drug License Required"
                    : "Partially Available"}
              </h3>
              <p className="rm-popup-message">{stockPopup.message}</p>
              {stockPopup.type === "partial" && stockPopup.details && (
                <div className="rm-popup-split-info">
                  <div className="rm-popup-split-row rm-popup-cart-row">
                    <span className="rm-popup-split-label">Added to Cart:</span>
                    <span className="rm-popup-split-value">
                      {stockPopup.details.cartQty} unit(s)
                    </span>
                  </div>
                  <div className="rm-popup-split-row rm-popup-request-row">
                    <span className="rm-popup-split-label">
                      Added to Request List:
                    </span>
                    <span className="rm-popup-split-value">
                      {stockPopup.details.requestQty} unit(s)
                    </span>
                  </div>
                </div>
              )}
              <button
                className="rm-popup-close-btn"
                onClick={() =>
                  setStockPopup({
                    show: false,
                    type: "",
                    message: "",
                    details: null,
                  })
                }>
                OK
              </button>
            </div>
          </div>
        )}
      </Container>
    </div>
  );
};

export default RequestMedicine;
